import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cashierEditSchema, type CashierEditInput } from '../routes/cashiers.schema';
import type { UserView } from '../api/users';

interface StoreOption { id: string; name: string }

/**
 * Edit dialog — same shape as CashierFormDialog but emits a PATCH payload and
 * treats email as read-only (per the spec: email is immutable; create a new
 * user if it needs to change).
 */
export function CashierEditDialog({
  open, onClose, onSubmit, cashier, stores,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (d: CashierEditInput) => void;
  cashier: UserView | null;
  stores: StoreOption[];
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<CashierEditInput>({
    resolver: zodResolver(cashierEditSchema),
    defaultValues: cashier ? {
      displayName: cashier.displayName,
      role: cashier.role,
      storeId: cashier.storeId ?? '',
    } : undefined,
    // re-init when the row changes — important when switching between rows
    // without unmounting the dialog.
    values: cashier ? {
      displayName: cashier.displayName,
      role: cashier.role,
      storeId: cashier.storeId ?? '',
    } : undefined,
  });

  if (!open || !cashier) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label="Edit cashier" className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded p-6 w-[420px] space-y-3">
        <h2 className="text-lg font-bold text-primary">Edit cashier</h2>
        <p className="text-xs text-ink/60">Email <span className="font-mono">{cashier.email}</span> cannot be changed.</p>
        <label className="block text-sm">
          Display name
          <input {...register('displayName')} className="mt-1 w-full border rounded p-2" />
        </label>
        {errors.displayName && <p className="text-sm text-accent">{errors.displayName.message}</p>}

        <label className="block text-sm">
          Role
          <select {...register('role')} className="mt-1 w-full border rounded p-2">
            <option value="CASHIER">Cashier</option>
            <option value="MANAGER">Manager</option>
          </select>
        </label>

        <label className="block text-sm">
          Store
          <select {...register('storeId')} className="mt-1 w-full border rounded p-2">
            <option value="">— select a store —</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        {errors.storeId && <p className="text-sm text-accent">{errors.storeId.message}</p>}

        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" className="rounded bg-primary px-4 py-2 text-white">Save</button>
        </div>
      </form>
    </div>
  );
}
